import { useRef } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import { Star, ArrowDown, Clock } from "lucide-react";
import { useDesktop } from "../hooks/useDesktop";
import { useBookingMode } from "../hooks/useBookingMode";
import {
  BRAND,
  HERO_IMG,
  HERO_SRCSET_JPG,
  HERO_SRCSET_WEBP,
  LOCATIONS,
} from "../data/site";

const LINES = ["THE", "VAULT", "BARBER CO."];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const isDesktop = useDesktop();
  const { bookProps } = useBookingMode();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.22]);

  const plate = (
    <picture>
      <source type="image/webp" srcSet={HERO_SRCSET_WEBP} sizes="100vw" />
      <img
        src={HERO_IMG}
        srcSet={HERO_SRCSET_JPG}
        sizes="100vw"
        width="1280"
        height="859"
        alt="Dark western barbershop interior with leather chairs and a Texas sunset through the storefront window"
        className="h-full w-full object-cover object-center"
        fetchPriority="high"
        decoding="async"
      />
    </picture>
  );

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-[#0a0d12] [@supports(min-height:100svh)]:min-h-[100svh]"
    >
      {isDesktop ? (
        <m.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          {plate}
        </m.div>
      ) : (
        <div className="absolute inset-0">{plate}</div>
      )}

      <div className="absolute inset-0 bg-[#0a0d12]/72" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d12] via-[#0a0d12]/35 to-[#0a0d12]/85" />
      <div className="vault-ridges absolute inset-y-0 left-0 w-[14%] opacity-60" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-end px-5 pb-16 pt-32 lg:px-10 lg:pb-24 [@supports(min-height:100svh)]:min-h-[100svh]">
        <div
          className="hero-fade mb-6 flex flex-wrap items-center gap-x-5 gap-y-3"
          style={{ animationDelay: "0.05s" }}
        >
          <span className="flex items-center gap-2 border border-[#c5a059]/35 bg-[#c5a059]/10 px-3 py-1.5">
            <Star size={12} className="fill-[#c5a059] text-[#c5a059]" />
            <span className="font-cond text-[11px] font-bold tracking-[0.2em] text-[#e2cf9e]">
              {BRAND.rating} ★ · {BRAND.reviewCount}+ GOOGLE REVIEWS
            </span>
          </span>
          <span className="label">Argyle &amp; Lantana, Texas</span>
        </div>

        <h1 className="font-cond text-[17vw] font-extrabold uppercase leading-[0.82] tracking-[-0.03em] sm:text-[14vw] lg:text-[11.5vw]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <span
                className={`hero-line block ${
                  i === 2 ? "brass-text" : "text-[#f4efe4]"
                }`}
                style={{ animationDelay: `${0.1 + i * 0.09}s` }}
              >
                {line}
              </span>
            </span>
          ))}
        </h1>

        <div
          className="hero-fade hairline mt-8 w-full max-w-3xl"
          style={{ animationDelay: "0.34s" }}
        />

        <div className="mt-8 grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-end">
          <p
            className="hero-fade max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base"
            style={{ animationDelay: "0.38s" }}
          >
            An elite barbershop offering full service at a fair price. Precision
            fades, beard sculpting and hot towel straight razor shaves, trusted
            by Texas Rangers{" "}
            <strong className="font-semibold text-[#e2cf9e]">Josh Jung</strong>{" "}
            and{" "}
            <strong className="font-semibold text-[#e2cf9e]">
              Kevin Pillar
            </strong>
            , and by every family in Argyle and Lantana. If quality matters,
            we're your shop.
          </p>

          <div
            className="hero-fade flex flex-col gap-4"
            style={{ animationDelay: "0.44s" }}
          >
            <div className="flex flex-wrap gap-3">
              {LOCATIONS.map((l) => (
                <a
                  key={l.id}
                  {...bookProps(l)}
                  data-testid={`hero-book-${l.id}`}
                  className={`sheen group flex min-h-[58px] flex-1 items-center justify-center whitespace-nowrap px-5 text-center font-cond text-[13px] font-bold uppercase tracking-[0.16em] transition-all duration-300 hover:-translate-y-0.5 sm:px-6 sm:tracking-[0.2em] ${
                    l.id === "argyle"
                      ? "bg-[#c5a059] text-[#0a0d12] hover:bg-[#d8b46b] hover:shadow-[0_14px_40px_-10px_rgba(197,160,89,0.65)]"
                      : "border border-white/20 text-slate-100 hover:border-[#c5a059] hover:text-[#d8b46b]"
                  }`}
                >
                  <span className="sheen-bar" />
                  Book {l.label}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-400">
              <Clock size={13} className="text-[#c5a059]" />
              {BRAND.hours} · Walk-ins welcome
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <button
                type="button"
                onClick={onBook}
                data-testid="hero-compare-locations"
                className="font-cond text-[11px] uppercase tracking-[0.24em] text-slate-400 underline decoration-[#c5a059]/40 underline-offset-4 transition-colors hover:text-[#d8b46b]"
              >
                Not sure which shop? Compare both
              </button>
              <a
                href="#enquiry"
                data-testid="hero-enquiry-link"
                className="font-cond text-[11px] uppercase tracking-[0.24em] text-[#c5a059] underline decoration-[#c5a059]/40 underline-offset-4 transition-colors hover:text-[#d8b46b]"
              >
                Rather not call? Leave your number
              </a>
            </div>
          </div>
        </div>

        <a
          href="#proof"
          data-testid="hero-scroll-cue"
          className="hero-fade mt-14 hidden items-center gap-3 font-cond text-[11px] uppercase tracking-[0.3em] text-slate-400 hover:text-[#d8b46b] lg:flex"
          style={{ animationDelay: "0.6s" }}
        >
          <ArrowDown size={14} />
          Scroll
        </a>
      </div>
    </section>
  );
};
